<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <v-card elevation="2" class="pa-4">
          <v-card-title class="text-h4 font-weight-bold primary--text">
            <v-icon large class="mr-3">mdi-workflow</v-icon>
            Workflow Management
          </v-card-title>
          <v-card-subtitle class="text-h6 grey--text">
            Manage complex processes and workflows
          </v-card-subtitle>
        </v-card>
      </v-col>
    </v-row>

    <v-row>
      <!-- Workflow Builder -->
      <v-col cols="12" md="8">
        <v-card elevation="2" class="pa-4">
          <v-card-title class="text-h5 font-weight-bold">
            <v-icon class="mr-2">mdi-sitemap</v-icon>
            Workflow Builder
          </v-card-title>
          
          <v-card-text>
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="workflowName"
                  label="Workflow Name"
                  outlined
                  dense
                  prepend-icon="mdi-format-title"
                ></v-text-field>
              </v-col>
              
              <v-col cols="12">
                <v-textarea
                  v-model="workflowDescription"
                  label="Description"
                  outlined
                  dense
                  rows="3"
                  prepend-icon="mdi-text"
                ></v-textarea>
              </v-col>
              
              <v-col cols="12" md="6">
                <v-select
                  v-model="workflowCategory"
                  :items="categories"
                  label="Category"
                  outlined
                  dense
                  prepend-icon="mdi-tag"
                ></v-select>
              </v-col>
            </v-row>

            <!-- Workflow Canvas -->
            <v-card outlined class="mt-4" style="min-height: 400px;">
              <v-card-title class="text-h6">
                <v-icon class="mr-2">mdi-diagram</v-icon>
                Workflow Canvas
              </v-card-title>
              
              <v-card-text>
                <div class="workflow-canvas" ref="workflowCanvas">
                  <div class="workflow-start">
                    <v-chip color="green" text-color="white" large>
                      <v-icon left>mdi-play</v-icon>
                      Start
                    </v-chip>
                  </div>
                  
                  <div 
                    class="workflow-canvas"
                    @drop="handleDrop"
                    @dragover="handleDragOver"
                    @dragenter="handleDragEnter"
                    @dragleave="handleDragLeave"
                  >
                    <!-- Workflow Flow Visualization -->
                    <div class="workflow-flow">
                      <div 
                        v-for="(step, index) in workflowSteps" 
                        :key="`step-${index}`"
                        class="workflow-step-container"
                        :class="{ 'drag-over': dragOverIndex === index }"
                      >
                        <!-- Drop Zone (before each step) -->
                        <div 
                          class="drop-zone"
                          :class="{ 'active': dragOverIndex === index && dragPosition === 'before' }"
                          @drop="handleDropAt(index, 'before')"
                          @dragover="handleDragOverAt(index, 'before')"
                          @dragenter="handleDragEnterAt(index, 'before')"
                          @dragleave="handleDragLeaveAt(index, 'before')"
                        >
                          <div class="drop-indicator">
                            <v-icon color="primary">mdi-drag-horizontal</v-icon>
                            <span class="text-caption">Drop here to insert before</span>
                          </div>
                        </div>
                        
                        <!-- Step Card -->
                        <div 
                          class="workflow-step"
                          :class="{ 
                            'selected': selectedStep === index,
                            'dragging': draggedStepIndex === index
                          }"
                          :draggable="true"
                          @dragstart="handleDragStart(index, $event)"
                          @dragend="handleDragEnd"
                          @click="selectStep(index)"
                        >
                          <v-card 
                            :color="step.type === 'decision' ? 'orange' : 'primary'"
                            :dark="step.type === 'decision'"
                            class="pa-3"
                            elevation="2"
                          >
                            <v-card-title class="text-subtitle-1 pa-2">
                              <v-icon class="mr-2">mdi-drag-vertical</v-icon>
                              <v-icon class="mr-2">{{ step.icon }}</v-icon>
                              {{ step.name }}
                              <v-spacer></v-spacer>
                              <v-chip small color="grey" text-color="white">
                                {{ index + 1 }}
                              </v-chip>
                            </v-card-title>
                            <v-card-text class="pa-2 text-caption">
                              {{ step.description }}
                            </v-card-text>
                            
                            <!-- Show decision paths if it's a decision step -->
                            <div v-if="step.type === 'decision' && (step.trueLabel || step.falseLabel)" class="pa-2">
                              <v-divider class="mb-2"></v-divider>
                              <div class="d-flex flex-column">
                                <div v-if="step.trueLabel" class="d-flex align-center mb-1">
                                  <v-icon small color="green" class="mr-1">mdi-check-circle</v-icon>
                                  <span class="text-caption">{{ step.trueLabel }}</span>
                                </div>
                                <div v-if="step.falseLabel" class="d-flex align-center">
                                  <v-icon small color="red" class="mr-1">mdi-close-circle</v-icon>
                                  <span class="text-caption">{{ step.falseLabel }}</span>
                                </div>
                              </div>
                            </div>
                          </v-card>
                        </div>

                        <!-- Flow Connections -->
                        <div v-if="index < workflowSteps.length - 1" class="flow-connections">
                          <!-- Normal Flow Arrow -->
                          <div class="flow-arrow normal-flow">
                            <v-icon color="primary" size="20">mdi-arrow-down</v-icon>
                            <span class="flow-label">Next</span>
                          </div>

                          <!-- Decision Flow Arrows (if current step is decision) -->
                          <div v-if="step.type === 'decision'" class="decision-flows">
                            <!-- True Path -->
                            <div v-if="step.trueAction && step.trueAction !== 'next'" class="flow-arrow true-flow">
                              <v-icon color="green" size="16">mdi-arrow-right</v-icon>
                              <span class="flow-label">{{ step.trueLabel || 'True' }}</span>
                              <div class="flow-target">
                                <v-chip x-small color="green" text-color="white">
                                  {{ getTargetStepName(step.trueTargetStep) }}
                                </v-chip>
                              </div>
                            </div>

                            <!-- False Path -->
                            <div v-if="step.falseAction && step.falseAction !== 'next'" class="flow-arrow false-flow">
                              <v-icon color="red" size="16">mdi-arrow-right</v-icon>
                              <span class="flow-label">{{ step.falseLabel || 'False' }}</span>
                              <div class="flow-target">
                                <v-chip x-small color="red" text-color="white">
                                  {{ getTargetStepName(step.falseTargetStep) }}
                                </v-chip>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <!-- Drop Zone (after last step) -->
                      <div 
                        class="drop-zone"
                        :class="{ 'active': dragOverIndex === workflowSteps.length }"
                        @drop="handleDropAt(workflowSteps.length, 'after')"
                        @dragover="handleDragOverAt(workflowSteps.length, 'after')"
                        @dragenter="handleDragEnterAt(workflowSteps.length, 'after')"
                        @dragleave="handleDragLeaveAt(workflowSteps.length, 'after')"
                      >
                        <div class="drop-indicator">
                          <v-icon color="primary">mdi-drag-horizontal</v-icon>
                          <span class="text-caption">Drop here to add at end</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div class="workflow-end">
                    <v-chip color="red" text-color="white" large>
                      <v-icon left>mdi-stop</v-icon>
                      End
                    </v-chip>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Toolbox -->
      <v-col cols="12" md="4">
        <v-card elevation="2" class="pa-4">
          <v-card-title class="text-h5 font-weight-bold">
            <v-icon class="mr-2">mdi-toolbox</v-icon>
            Toolbox
          </v-card-title>
          
          <v-card-text>
            <v-row>
              <v-col cols="12">
                <v-btn
                  block
                  color="primary"
                  outlined
                  @click="addStep('task')"
                  class="mb-2"
                >
                  <v-icon left>mdi-checkbox-marked-circle</v-icon>
                  Add Task
                </v-btn>
                
                <v-btn
                  block
                  color="orange"
                  outlined
                  @click="addStep('decision')"
                  class="mb-2"
                >
                  <v-icon left>mdi-help-circle</v-icon>
                  Add Decision
                </v-btn>
                
                <v-btn
                  block
                  color="purple"
                  outlined
                  @click="addStep('approval')"
                  class="mb-2"
                >
                  <v-icon left>mdi-account-check</v-icon>
                  Add Approval
                </v-btn>
                
                <v-btn
                  block
                  color="teal"
                  outlined
                  @click="addStep('notification')"
                  class="mb-2"
                >
                  <v-icon left>mdi-bell</v-icon>
                  Add Notification
                </v-btn>
              </v-col>
            </v-row>
            
            <v-divider class="my-3"></v-divider>
            
            <v-row>
              <v-col cols="12">
                <v-btn
                  block
                  color="secondary"
                  outlined
                  @click="createExampleWorkflow"
                  class="mb-2"
                >
                  <v-icon left>mdi-lightbulb</v-icon>
                  Create Example Workflow
                </v-btn>
              </v-col>
            </v-row>

            <!-- Step Properties -->
            <v-divider class="my-4"></v-divider>
            
            <div v-if="selectedStep !== null">
              <v-card-title class="text-h6 pa-0">
                Step Properties
              </v-card-title>
              
              <v-text-field
                v-model="workflowSteps[selectedStep].name"
                label="Name"
                outlined
                dense
                class="mt-2"
              ></v-text-field>
              
              <v-textarea
                v-model="workflowSteps[selectedStep].description"
                label="Description"
                outlined
                dense
                rows="2"
              ></v-textarea>
              
              <v-select
                v-model="workflowSteps[selectedStep].assignee"
                :items="assignees"
                label="Assignee"
                outlined
                dense
              ></v-select>
              
              <v-text-field
                v-model="workflowSteps[selectedStep].dueDate"
                label="Due Date (days)"
                type="number"
                outlined
                dense
              ></v-text-field>

              <!-- Conditional Logic for Decision Steps -->
              <div v-if="workflowSteps[selectedStep].type === 'decision'" class="mt-4">
                <v-divider class="mb-3"></v-divider>
                <v-card-title class="text-subtitle-1 pa-0 mb-2">
                  <v-icon class="mr-2">mdi-logic</v-icon>
                  Conditional Logic
                </v-card-title>
                
                <v-expansion-panels>
                  <v-expansion-panel>
                    <v-expansion-panel-header>
                      <v-icon class="mr-2">mdi-plus-circle</v-icon>
                      Add Condition
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <v-row>
                        <v-col cols="12" md="4">
                          <v-select
                            v-model="newCondition.field"
                            :items="conditionFields"
                            label="Field"
                            outlined
                            dense
                          ></v-select>
                        </v-col>
                        <v-col cols="12" md="3">
                          <v-select
                            v-model="newCondition.operator"
                            :items="conditionOperators"
                            label="Operator"
                            outlined
                            dense
                          ></v-select>
                        </v-col>
                        <v-col cols="12" md="4">
                          <v-text-field
                            v-model="newCondition.value"
                            label="Value"
                            outlined
                            dense
                          ></v-text-field>
                        </v-col>
                        <v-col cols="12" md="1">
                          <v-btn
                            color="primary"
                            @click="addCondition"
                            icon
                            small
                          >
                            <v-icon>mdi-plus</v-icon>
                          </v-btn>
                        </v-col>
                      </v-row>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>

                <!-- Existing Conditions -->
                <div v-if="workflowSteps[selectedStep].conditions && workflowSteps[selectedStep].conditions.length > 0" class="mt-3">
                  <v-chip
                    v-for="(condition, index) in workflowSteps[selectedStep].conditions"
                    :key="index"
                    class="ma-1"
                    color="primary"
                    close
                    @click:close="removeCondition(index)"
                  >
                    {{ condition.field }} {{ condition.operator }} {{ condition.value }}
                  </v-chip>
                </div>

                <!-- True Path Configuration -->
                <v-card class="mt-3" outlined>
                  <v-card-title class="text-subtitle-2 pa-3">
                    <v-icon class="mr-2" color="green">mdi-check-circle</v-icon>
                    Path A - If condition is TRUE
                  </v-card-title>
                  <v-card-text class="pt-0">
                    <v-select
                      v-model="workflowSteps[selectedStep].trueAction"
                      :items="conditionActions"
                      label="Action"
                      outlined
                      dense
                      class="mb-2"
                    ></v-select>
                    
                    <v-text-field
                      v-if="workflowSteps[selectedStep].trueAction === 'specific'"
                      v-model="workflowSteps[selectedStep].trueTargetStep"
                      label="Target Step Name"
                      outlined
                      dense
                      hint="Enter the name of the step to go to"
                    ></v-text-field>
                    
                    <v-text-field
                      v-model="workflowSteps[selectedStep].trueLabel"
                      label="Path Label (optional)"
                      outlined
                      dense
                      hint="e.g., 'Approved Path', 'High Priority'"
                    ></v-text-field>
                  </v-card-text>
                </v-card>

                <!-- False Path Configuration -->
                <v-card class="mt-3" outlined>
                  <v-card-title class="text-subtitle-2 pa-3">
                    <v-icon class="mr-2" color="red">mdi-close-circle</v-icon>
                    Path B - If condition is FALSE
                  </v-card-title>
                  <v-card-text class="pt-0">
                    <v-select
                      v-model="workflowSteps[selectedStep].falseAction"
                      :items="conditionActions"
                      label="Action"
                      outlined
                      dense
                      class="mb-2"
                    ></v-select>
                    
                    <v-text-field
                      v-if="workflowSteps[selectedStep].falseAction === 'specific'"
                      v-model="workflowSteps[selectedStep].falseTargetStep"
                      label="Target Step Name"
                      outlined
                      dense
                      hint="Enter the name of the step to go to"
                    ></v-text-field>
                    
                    <v-text-field
                      v-model="workflowSteps[selectedStep].falseLabel"
                      label="Path Label (optional)"
                      outlined
                      dense
                      hint="e.g., 'Rejected Path', 'Standard Process'"
                    ></v-text-field>
                  </v-card-text>
                </v-card>
              </div>
              
              <v-btn
                color="red"
                outlined
                block
                @click="removeStep(selectedStep)"
                class="mt-2"
              >
                <v-icon left>mdi-delete</v-icon>
                Remove Step
              </v-btn>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Action Buttons -->
    <v-row class="mt-4">
      <v-col cols="12">
        <v-card elevation="2" class="pa-4">
          <v-card-actions>
            <v-btn
              color="success"
              large
              @click="saveWorkflow"
              :disabled="workflowSteps.length === 0"
            >
              <v-icon left>mdi-content-save</v-icon>
              Save Workflow
            </v-btn>
            
            <v-btn
              color="info"
              large
              @click="previewWorkflow"
              :disabled="workflowSteps.length === 0"
              class="ml-2"
            >
              <v-icon left>mdi-eye</v-icon>
              Preview
            </v-btn>
            
            <v-btn
              color="success"
              large
              @click="activateWorkflow"
              :disabled="workflowSteps.length === 0 || !selectedWorkflow"
              class="ml-2"
            >
              <v-icon left>mdi-play</v-icon>
              Activate
            </v-btn>
            
            <v-btn
              color="warning"
              large
              @click="exportWorkflow"
              :disabled="workflowSteps.length === 0"
              class="ml-2"
            >
              <v-icon left>mdi-download</v-icon>
              Export
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>

    <!-- Workflow List -->
    <v-row class="mt-4">
      <v-col cols="12">
        <v-card elevation="2" class="pa-4">
          <v-card-title class="text-h5 font-weight-bold">
            <v-icon class="mr-2">mdi-format-list-bulleted</v-icon>
            Saved Workflows
          </v-card-title>
          
          <!-- Search and Filters -->
          <v-card-text>
            <v-row class="mb-4">
              <v-col cols="12" md="3">
                <v-text-field
                  v-model="searchQuery"
                  label="Search workflows"
                  outlined
                  dense
                  prepend-icon="mdi-magnify"
                  @input="searchWorkflows"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="2">
                <v-btn
                  color="primary"
                  @click="refreshWorkflowsList"
                  block
                  outlined
                >
                  <v-icon left>mdi-refresh</v-icon>
                  Refresh
                </v-btn>
              </v-col>
              <v-col cols="12" md="3">
                <v-select
                  v-model="statusFilter"
                  :items="[
                    { text: 'All Status', value: 'all' },
                    { text: 'Draft', value: 'draft' },
                    { text: 'Active', value: 'active' },
                    { text: 'Inactive', value: 'inactive' },
                    { text: 'Archived', value: 'archived' }
                  ]"
                  label="Status"
                  outlined
                  dense
                  @change="searchWorkflows"
                ></v-select>
              </v-col>
              <v-col cols="12" md="3">
                <v-select
                  v-model="categoryFilter"
                  :items="[
                    { text: 'All Categories', value: 'all' },
                    ...categories
                  ]"
                  label="Category"
                  outlined
                  dense
                  @change="searchWorkflows"
                ></v-select>
              </v-col>
              <v-col cols="12" md="2">
                <v-btn
                  color="primary"
                  block
                  @click="resetForm"
                  class="mt-1"
                >
                  <v-icon left>mdi-plus</v-icon>
                  New
                </v-btn>
              </v-col>
            </v-row>
            
            <v-data-table
              :key="tableKey"
              :headers="workflowHeaders"
              :items="savedWorkflows"
              :items-per-page="10"
              :loading="loading"
              class="elevation-1"
            >
              <template v-slot:item.status="{ item }">
                <v-chip
                  :color="getStatusColor(item.status)"
                  small
                  text-color="white"
                >
                  {{ getStatusText(item.status) }}
                </v-chip>
              </template>
              
              <template v-slot:item.category="{ item }">
                <v-chip
                  color="primary"
                  small
                  outlined
                >
                  {{ getCategoryText(item.category) }}
                </v-chip>
              </template>
              
              <template v-slot:item.createdAt="{ item }">
                {{ formatDate(item.createdAt) }}
              </template>
              
              <template v-slot:item.actions="{ item }">
                <v-btn
                  small
                  color="primary"
                  @click="loadWorkflow(item)"
                  class="mr-1"
                  icon
                >
                  <v-icon small>mdi-pencil</v-icon>
                </v-btn>
                <v-btn
                  small
                  color="success"
                  @click="executeWorkflow(item)"
                  class="mr-1"
                  icon
                  :disabled="item.status !== 'active'"
                >
                  <v-icon small>mdi-play</v-icon>
                </v-btn>
                <v-btn
                  small
                  color="info"
                  @click="duplicateWorkflow(item)"
                  class="mr-1"
                  icon
                >
                  <v-icon small>mdi-content-duplicate</v-icon>
                </v-btn>
                <v-btn
                  small
                  color="red"
                  @click="deleteWorkflow(item)"
                  icon
                >
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Insert Step Dialog -->
    <v-dialog v-model="showInsertDialog" max-width="500px">
      <v-card>
        <v-card-title class="text-h5">
          <v-icon class="mr-2">mdi-plus-circle</v-icon>
          Insert Step
        </v-card-title>
        
        <v-card-text>
          <v-row>
            <v-col cols="12">
              <v-select
                v-model="insertStepType"
                :items="[
                  { text: 'Task', value: 'task' },
                  { text: 'Decision', value: 'decision' },
                  { text: 'Approval', value: 'approval' },
                  { text: 'Notification', value: 'notification' }
                ]"
                label="Step Type"
                outlined
                dense
              ></v-select>
            </v-col>
            
            <v-col cols="12">
              <v-text-field
                v-model="insertStepName"
                label="Step Name"
                outlined
                dense
                :placeholder="getDefaultStepName(insertStepType)"
              ></v-text-field>
            </v-col>
            
            <v-col cols="12">
              <v-textarea
                v-model="insertStepDescription"
                label="Description"
                outlined
                dense
                rows="2"
                :placeholder="getDefaultStepDescription(insertStepType)"
              ></v-textarea>
            </v-col>
            
            <v-col cols="12">
              <v-select
                v-model="insertStepAssignee"
                :items="assignees"
                label="Assignee"
                outlined
                dense
              ></v-select>
            </v-col>
            
            <v-col cols="12">
              <v-text-field
                v-model="insertStepDueDate"
                label="Due Date (days)"
                type="number"
                outlined
                dense
                :placeholder="getDefaultDueDate(insertStepType)"
              ></v-text-field>
            </v-col>
          </v-row>
        </v-card-text>
        
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="grey"
            text
            @click="showInsertDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            @click="insertStep"
          >
            Insert Step
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Workflow Preview Dialog -->
    <v-dialog v-model="showWorkflowPreview" max-width="800" scrollable>
      <v-card>
        <v-card-title class="text-h5 primary--text">
          <v-icon class="mr-2">mdi-eye</v-icon>
          Workflow Preview
        </v-card-title>
        
        <v-card-text v-if="workflowPreview">
          <div class="workflow-preview">
            <v-row>
              <v-col cols="12">
                <h3 class="text-h6 mb-2">{{ workflowPreview.name }}</h3>
                <p class="text-body-2 grey--text mb-4">{{ workflowPreview.description }}</p>
                <v-chip color="primary" class="mb-4">
                  Category: {{ workflowPreview.category }}
                </v-chip>
              </v-col>
            </v-row>
            
            <v-divider class="my-4"></v-divider>
            
            <div class="workflow-steps-preview">
              <h4 class="text-subtitle-1 mb-3">Workflow Steps:</h4>
              <div 
                v-for="(step, index) in workflowPreview.steps" 
                :key="index"
                class="step-preview mb-3"
              >
                <v-card outlined>
                  <v-card-text>
                    <div class="d-flex align-center mb-2">
                      <v-chip small color="primary" class="mr-2">{{ index + 1 }}</v-chip>
                      <v-icon class="mr-2">{{ step.icon }}</v-icon>
                      <strong>{{ step.name }}</strong>
                      <v-spacer></v-spacer>
                      <v-chip small :color="step.type === 'decision' ? 'orange' : 'primary'">
                        {{ step.type.toUpperCase() }}
                      </v-chip>
                    </div>
                    <p class="text-caption mb-2">{{ step.description }}</p>
                    <div class="d-flex align-center text-caption">
                      <v-icon small class="mr-1">mdi-account</v-icon>
                      <span class="mr-3">{{ step.assignee }}</span>
                      <v-icon small class="mr-1">mdi-clock</v-icon>
                      <span>{{ step.dueDate }} days</span>
                    </div>
                    
                    <!-- Show decision paths if it's a decision step -->
                    <div v-if="step.type === 'decision' && (step.trueLabel || step.falseLabel)" class="mt-3">
                      <v-divider class="mb-2"></v-divider>
                      <div class="d-flex flex-column">
                        <div v-if="step.trueLabel" class="d-flex align-center mb-1">
                          <v-icon small color="green" class="mr-1">mdi-check-circle</v-icon>
                          <span class="text-caption">{{ step.trueLabel }}</span>
                          <span class="text-caption ml-2">→ {{ getTargetStepName(step.trueTargetStep) }}</span>
                        </div>
                        <div v-if="step.falseLabel" class="d-flex align-center">
                          <v-icon small color="red" class="mr-1">mdi-close-circle</v-icon>
                          <span class="text-caption">{{ step.falseLabel }}</span>
                          <span class="text-caption ml-2">→ {{ getTargetStepName(step.falseTargetStep) }}</span>
                        </div>
                      </div>
                    </div>
                  </v-card-text>
                </v-card>
              </div>
            </div>
            
            <v-divider class="my-4"></v-divider>
            
            <div class="workflow-summary">
              <h4 class="text-subtitle-1 mb-3">Workflow Summary:</h4>
              <v-row>
                <v-col cols="6">
                  <v-card outlined>
                    <v-card-text>
                      <div class="text-center">
                        <v-icon large color="primary" class="mb-2">mdi-format-list-numbered</v-icon>
                        <div class="text-h6">{{ workflowPreview.steps.length }}</div>
                        <div class="text-caption">Total Steps</div>
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="6">
                  <v-card outlined>
                    <v-card-text>
                      <div class="text-center">
                        <v-icon large color="orange" class="mb-2">mdi-help-circle</v-icon>
                        <div class="text-h6">{{ workflowPreview.decisionSteps }}</div>
                        <div class="text-caption">Decision Points</div>
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </div>
          </div>
        </v-card-text>
        
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="showWorkflowPreview = false">
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import axios from "axios";

// Add axios interceptors for debugging
axios.interceptors.request.use(
  function (config) {
    console.log('🚀 Axios Request:', config.method?.toUpperCase(), config.url);
    return config;
  },
  function (error) {
    console.error('❌ Axios Request Error:', error);
    return Promise.reject(error);
  }
);

axios.interceptors.response.use(
  function (response) {
    console.log('✅ Axios Response:', response.status, response.config.url);
    return response;
  },
  function (error) {
    console.error('❌ Axios Response Error:', error.response?.status, error.config?.url, error.message);
    return Promise.reject(error);
  }
);

export default {
  name: "Workflow",
  data() {
    return {
      workflowName: "",
      workflowDescription: "",
      workflowCategory: "general",
      selectedStep: null,
      workflowSteps: [],
      assignees: [
        "System",
        "Administrator",
        "Agent",
        "Credentialing",
        "Member Services"
      ],
      categories: [
        { text: "General", value: "general" },
        { text: "Credentialing", value: "credentialing" },
        { text: "Enrollment", value: "enrollment" },
        { text: "Authorization", value: "authorization" },
        { text: "Correspondence", value: "correspondence" }
      ],
      workflowHeaders: [
        { text: "Name", value: "name" },
        { text: "Description", value: "description" },
        { text: "Category", value: "category" },
        { text: "Status", value: "status" },
        { text: "Steps", value: "stepCount" },
        { text: "Created At", value: "createdAt" },
        { text: "Actions", value: "actions", sortable: false }
      ],
      savedWorkflows: [],
      loading: false,
      selectedWorkflow: null,
      showWorkflowDialog: false,
      tableKey: 0,
      searchQuery: "",
      statusFilter: "all",
      categoryFilter: "all",
      // Insert step dialog
      showInsertDialog: false,
      insertPosition: 0,
      insertStepType: 'task',
      insertStepName: '',
      insertStepDescription: '',
      insertStepAssignee: 'System',
      insertStepDueDate: 7,
      // Drag and drop
      draggedStepIndex: null,
      dragOverIndex: null,
      dragPosition: null,
      // Preview
      showWorkflowPreview: false,
      workflowPreview: null,
      // Conditional logic variables
      newCondition: {
        field: '',
        operator: '',
        value: ''
      },
      conditionFields: [
        { text: 'Patient Age', value: 'patient.age' },
        { text: 'Enrollment Status', value: 'enrollment.status' },
        { text: 'Provider Type', value: 'provider.type' },
        { text: 'Amount', value: 'amount' },
        { text: 'Priority', value: 'priority' },
        { text: 'Department', value: 'department' }
      ],
      conditionOperators: [
        { text: 'Equals', value: '==' },
        { text: 'Not Equals', value: '!=' },
        { text: 'Greater Than', value: '>' },
        { text: 'Less Than', value: '<' },
        { text: 'Greater or Equal', value: '>=' },
        { text: 'Less or Equal', value: '<=' },
        { text: 'Contains', value: 'contains' },
        { text: 'Starts With', value: 'startsWith' }
      ],
      conditionActions: [
        { text: 'Continue to Next Step', value: 'next' },
        { text: 'Skip to End', value: 'end' },
        { text: 'Go to Specific Step', value: 'specific' },
        { text: 'Go to Approval Path', value: 'approval_path' },
        { text: 'Go to Rejection Path', value: 'rejection_path' },
        { text: 'Go to High Priority Path', value: 'high_priority_path' },
        { text: 'Go to Standard Path', value: 'standard_path' },
        { text: 'Send Notification', value: 'notification' }
      ]
    };
  },
  watch: {
    savedWorkflows: {
      handler(newVal) {
        console.log('Workflows list changed:', newVal.length, 'items');
        this.tableKey++;
      },
      deep: true
    }
  },
  async mounted() {
    console.log('🚀 Component mounted, checking authentication...');
    await this.checkAuthStatus();
    this.loadWorkflows();
  },
  methods: {
    async checkAuthStatus() {
      try {
        console.log('🔐 Checking authentication status...');
        const response = await axios.get('/api/user');
        console.log('✅ User authenticated:', response.data);
        return true;
      } catch (error) {
        console.error('❌ User not authenticated:', error.response?.status, error.message);
        this.$toast?.error('Please log in to access workflows') || console.error('❌ Please log in to access workflows');
        return false;
      }
    },

    async loadWorkflows() {
      try {
        console.log('Loading workflows...');
        this.loading = true;
        const response = await axios.get('/api/workflows');
        console.log('Workflows response:', response.data);
        
        // Use Vue.set to ensure reactivity
        this.$set(this, 'savedWorkflows', [...response.data]);
        console.log('Workflows loaded:', this.savedWorkflows.length);
        
        // Force table re-render
        this.tableKey++;
        
        // Force component update
        await this.$nextTick();
        this.$forceUpdate();
      } catch (error) {
        console.error('Error loading workflows:', error);
        this.$toast?.error('Error loading workflows') || console.error('❌ Error loading workflows');
      } finally {
        this.loading = false;
      }
    },

    addStep(type) {
      const stepTypes = {
        task: {
          name: "New Task",
          description: "Task description",
          icon: "mdi-checkbox-marked-circle",
          type: "task",
          assignee: "System",
          dueDate: 7
        },
        decision: {
          name: "New Decision",
          description: "Decision point in workflow",
          icon: "mdi-help-circle",
          type: "decision",
          assignee: "System",
          dueDate: 3,
          conditions: [],
          trueAction: "next",
          falseAction: "next",
          trueTargetStep: "",
          falseTargetStep: "",
          trueLabel: "",
          falseLabel: ""
        },
        approval: {
          name: "New Approval",
          description: "Approval process",
          icon: "mdi-account-check",
          type: "approval",
          assignee: "Administrator",
          dueDate: 5
        },
        notification: {
          name: "New Notification",
          description: "Send notification",
          icon: "mdi-bell",
          type: "notification",
          assignee: "System",
          dueDate: 1
        }
      };
      
      this.workflowSteps.push(stepTypes[type]);
    },
    
    selectStep(index) {
      this.selectedStep = index;
    },
    
    removeStep(index) {
      this.workflowSteps.splice(index, 1);
      this.selectedStep = null;
    },
    
    async saveWorkflow() {
      console.log('Save workflow clicked');
      console.log('Workflow name:', this.workflowName);
      console.log('Workflow steps:', this.workflowSteps.length);
      
      if (!this.workflowName || this.workflowSteps.length === 0) {
        this.$toast?.error("Please fill in the name and add at least one step!") || console.error("❌ Please fill in the name and add at least one step!");
        return;
      }

      try {
        this.loading = true;
        const workflowData = {
          name: this.workflowName,
          description: this.workflowDescription,
          category: this.workflowCategory,
          steps: this.workflowSteps,
          status: 'draft'
        };

        console.log('Sending workflow data:', workflowData);

        if (this.selectedWorkflow) {
          // Update existing workflow
          console.log('Updating existing workflow:', this.selectedWorkflow._id);
          await axios.put(`/api/workflows/${this.selectedWorkflow._id}`, workflowData);
          this.$toast?.success("Workflow updated successfully!") || console.log("✅ Workflow updated successfully!");
        } else {
          // Create new workflow
          console.log('Creating new workflow');
          const response = await axios.post('/api/workflows', workflowData);
          console.log('Workflow created:', response.data);
          this.$toast?.success("Workflow saved successfully!") || console.log("✅ Workflow saved successfully!");
        }
        
        // Reset form
        this.resetForm();
        await this.loadWorkflows();
      } catch (error) {
        console.error('Error saving workflow:', error);
        console.error('Error response:', error.response?.data);
        this.$toast?.error(`Error saving workflow: ${error.response?.data?.message || error.message}`) || console.error(`❌ Error saving workflow: ${error.response?.data?.message || error.message}`);
      } finally {
        this.loading = false;
      }
    },

    async activateWorkflow() {
      if (!this.selectedWorkflow) {
        this.$toast?.error("Please select a workflow to activate") || console.error("❌ Please select a workflow to activate");
        return;
      }

      try {
        this.loading = true;
        await axios.put(`/api/workflows/${this.selectedWorkflow._id}`, {
          ...this.selectedWorkflow,
          status: 'active'
        });
        
        this.$toast?.success("Workflow activated successfully!") || console.log("✅ Workflow activated successfully!");
        await this.loadWorkflows();
      } catch (error) {
        console.error('Error activating workflow:', error);
        this.$toast?.error('Error activating workflow') || console.error('❌ Error activating workflow');
      } finally {
        this.loading = false;
      }
    },

    resetForm() {
      console.log('Resetting form');
      this.workflowName = "";
      this.workflowDescription = "";
      this.workflowCategory = "general";
      this.workflowSteps = [];
      this.selectedStep = null;
      this.selectedWorkflow = null;
      console.log('Form reset complete');
    },
    
    loadWorkflow(item) {
      console.log('Loading workflow:', item);
      this.selectedWorkflow = item;
      this.workflowName = item.name;
      this.workflowDescription = item.description;
      this.workflowCategory = item.category;
      this.workflowSteps = item.steps || [];
      this.selectedStep = null;
      console.log('Workflow loaded - Name:', this.workflowName, 'Steps:', this.workflowSteps.length);
    },
    
    async executeWorkflow(item) {
      try {
        this.loading = true;
        await axios.post(`/api/workflows/${item._id}/execute`, {
          context: {},
          priority: 'normal'
        });
        this.$toast?.success(`Workflow "${item.name}" executed successfully!`) || console.log(`✅ Workflow "${item.name}" executed successfully!`);
        await this.loadWorkflows();
      } catch (error) {
        console.error('Error executing workflow:', error);
        this.$toast?.error('Error executing workflow') || console.error('❌ Error executing workflow');
      } finally {
        this.loading = false;
      }
    },

    async refreshWorkflowsList() {
      console.log('🔄 Refreshing workflows list...');
      console.log('Current workflows count:', this.savedWorkflows.length);
      
      try {
        console.log('📡 Making API call to /api/workflows');
        const response = await axios.get('/api/workflows');
        console.log('✅ API response received:', response.status, response.data);
        console.log('📊 Response data length:', response.data.length);
        
        // Use Vue.set to ensure reactivity
        this.$set(this, 'savedWorkflows', [...response.data]);
        console.log('💾 Workflows stored in component:', this.savedWorkflows.length);
        
        // Force table re-render
        this.tableKey++;
        console.log('🔑 Table key updated to:', this.tableKey);
        
        // Force component update
        await this.$nextTick();
        this.$forceUpdate();
        
        console.log('✅ Workflows list refreshed successfully');
        this.$toast?.success(`List updated with ${this.savedWorkflows.length} workflows`) || console.log(`✅ List updated with ${this.savedWorkflows.length} workflows`);
      } catch (error) {
        console.error('❌ Error refreshing workflows list:', error);
        console.error('Error details:', error.response?.data);
        console.error('Error status:', error.response?.status);
        this.$toast?.error(`Error refreshing workflows list: ${error.message}`) || console.error(`❌ Error refreshing workflows list: ${error.message}`);
      }
    },

    async deleteWorkflow(item) {
      if (!confirm(`Are you sure you want to delete the workflow "${item.name}"?`)) {
        console.log('❌ Delete cancelled by user');
        return;
      }

      try {
        console.log('🗑️ Starting delete workflow:', item._id, item.name);
        console.log('📊 Current workflows before delete:', this.savedWorkflows.length);
        this.loading = true;
        
        console.log('📡 Making DELETE API call to /api/workflows/' + item._id);
        await axios.delete(`/api/workflows/${item._id}`);
        console.log('✅ Workflow deleted successfully from API');
        
        this.$toast?.success("Workflow removed successfully!") || console.log("✅ Workflow removed successfully!");
        
        // Use the dedicated refresh method with delay
        console.log('⏰ Setting timeout for refresh in 500ms...');
        setTimeout(async () => {
          console.log('🔄 Timeout triggered, calling refreshWorkflowsList...');
          await this.refreshWorkflowsList();
          console.log('📊 Workflows after refresh:', this.savedWorkflows.length);
        }, 500);
      } catch (error) {
        console.error('❌ Error deleting workflow:', error);
        console.error('Error details:', error.response?.data);
        console.error('Error status:', error.response?.status);
        this.$toast?.error(`Error removing workflow: ${error.message}`) || console.error(`❌ Error removing workflow: ${error.message}`);
      } finally {
        this.loading = false;
      }
    },
    
    previewWorkflow() {
      console.log('Preview workflow clicked');
      console.log('Workflow name:', this.workflowName);
      console.log('Workflow steps:', this.workflowSteps.length);
      
      if (!this.workflowName || this.workflowSteps.length === 0) {
        this.$toast?.error("Please fill in the name and add at least one step!") || console.error("❌ Please fill in the name and add at least one step!");
        return;
      }

      try {
        // Create a comprehensive workflow summary
        const summary = this.generateWorkflowSummary();
        console.log('Generated summary:', summary);
        
        // Show preview in a dialog
        this.showWorkflowPreview = true;
        this.workflowPreview = summary;
        console.log('Preview dialog should be open now');
      } catch (error) {
        console.error('Error in preview:', error);
        this.$toast?.error('Error generating preview') || console.error('❌ Error generating preview');
      }
    },
    
    async exportWorkflow() {
      try {
        const response = await axios.get(`/api/workflows/${this.selectedWorkflow._id}/export`, {
          responseType: 'blob'
        });
        
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${this.workflowName || 'workflow'}.json`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        
        this.$toast?.success("Workflow exported successfully!") || console.log("✅ Workflow exported successfully!");
      } catch (error) {
        console.error('Error exporting workflow:', error);
        this.$toast?.error('Error exporting workflow') || console.error('❌ Error exporting workflow');
      }
    },

    async duplicateWorkflow(item) {
      try {
        console.log('Duplicating workflow:', item._id);
        this.loading = true;
        await axios.post(`/api/workflows/${item._id}/duplicate`);
        console.log('Workflow duplicated successfully');
        this.$toast?.success("Workflow duplicated successfully!") || console.log("✅ Workflow duplicated successfully!");
        
        // Use the dedicated refresh method with delay
        setTimeout(async () => {
          await this.refreshWorkflowsList();
          console.log('Workflows after refresh:', this.savedWorkflows.length);
        }, 500);
      } catch (error) {
        console.error('Error duplicating workflow:', error);
        this.$toast?.error('Error duplicating workflow') || console.error('❌ Error duplicating workflow');
      } finally {
        this.loading = false;
      }
    },

    async searchWorkflows() {
      try {
        this.loading = true;
        const params = new URLSearchParams();
        if (this.searchQuery) params.append('search', this.searchQuery);
        if (this.statusFilter !== 'all') params.append('status', this.statusFilter);
        if (this.categoryFilter !== 'all') params.append('category', this.categoryFilter);
        
        const response = await axios.get(`/api/workflows?${params}`);
        // Use Vue.set to ensure reactivity
        this.$set(this, 'savedWorkflows', [...response.data]);
        
        // Force table re-render
        this.tableKey++;
        
        // Force component update
        await this.$nextTick();
        this.$forceUpdate();
      } catch (error) {
        console.error('Error searching workflows:', error);
        this.$toast?.error('Error searching workflows') || console.error('❌ Error searching workflows');
      } finally {
        this.loading = false;
      }
    },

    getStatusColor(status) {
      const colors = {
        draft: 'grey',
        active: 'green',
        inactive: 'orange',
        archived: 'red'
      };
      return colors[status] || 'grey';
    },

    getStatusText(status) {
      const texts = {
        draft: 'Draft',
        active: 'Active',
        inactive: 'Inactive',
        archived: 'Archived'
      };
      return texts[status] || status;
    },

    getCategoryText(category) {
      const categoryObj = this.categories.find(c => c.value === category);
      return categoryObj ? categoryObj.text : category;
    },

    formatDate(date) {
      if (!date) return '';
      return new Date(date).toLocaleDateString('pt-BR');
    },

    // Conditional logic methods
    addCondition() {
      if (this.newCondition.field && this.newCondition.operator && this.newCondition.value) {
        if (!this.workflowSteps[this.selectedStep].conditions) {
          this.$set(this.workflowSteps[this.selectedStep], 'conditions', []);
        }
        
        this.workflowSteps[this.selectedStep].conditions.push({
          field: this.newCondition.field,
          operator: this.newCondition.operator,
          value: this.newCondition.value
        });
        
        // Reset form
        this.newCondition = {
          field: '',
          operator: '',
          value: ''
        };
      }
    },

    removeCondition(index) {
      this.workflowSteps[this.selectedStep].conditions.splice(index, 1);
    },

    // Generate workflow summary with paths
    generateWorkflowSummary() {
      const decisionSteps = this.workflowSteps.filter(step => step.type === 'decision');
      const decisionPaths = decisionSteps.map(step => ({
        step: step.name,
        truePath: step.trueLabel || 'True',
        falsePath: step.falseLabel || 'False',
        trueAction: step.trueAction,
        falseAction: step.falseAction
      }));

      return {
        name: this.workflowName,
        description: this.workflowDescription,
        category: this.workflowCategory,
        totalSteps: this.workflowSteps.length,
        decisionSteps: decisionSteps.length,
        steps: this.workflowSteps,
        decisionPaths: decisionPaths
      };
    },

    // Insert step methods
    showInsertStepDialog(position) {
      this.insertPosition = position;
      this.insertStepType = 'task';
      this.insertStepName = '';
      this.insertStepDescription = '';
      this.insertStepAssignee = 'System';
      this.insertStepDueDate = 7;
      this.showInsertDialog = true;
    },

    insertStep() {
      if (!this.insertStepName.trim()) {
        this.$toast?.error('Please enter a step name') || console.error('❌ Please enter a step name');
        return;
      }

      const stepTypes = {
        task: {
          name: this.insertStepName || "New Task",
          description: this.insertStepDescription || "Task description",
          icon: "mdi-checkbox-marked-circle",
          type: "task",
          assignee: this.insertStepAssignee,
          dueDate: parseInt(this.insertStepDueDate) || 7
        },
        decision: {
          name: this.insertStepName || "New Decision",
          description: this.insertStepDescription || "Decision point in workflow",
          icon: "mdi-help-circle",
          type: "decision",
          assignee: this.insertStepAssignee,
          dueDate: parseInt(this.insertStepDueDate) || 3,
          conditions: [],
          trueAction: "next",
          falseAction: "next",
          trueTargetStep: "",
          falseTargetStep: "",
          trueLabel: "",
          falseLabel: ""
        },
        approval: {
          name: this.insertStepName || "New Approval",
          description: this.insertStepDescription || "Approval process",
          icon: "mdi-account-check",
          type: "approval",
          assignee: this.insertStepAssignee,
          dueDate: parseInt(this.insertStepDueDate) || 5
        },
        notification: {
          name: this.insertStepName || "New Notification",
          description: this.insertStepDescription || "Send notification",
          icon: "mdi-bell",
          type: "notification",
          assignee: this.insertStepAssignee,
          dueDate: parseInt(this.insertStepDueDate) || 1
        }
      };

      const newStep = stepTypes[this.insertStepType];
      this.workflowSteps.splice(this.insertPosition, 0, newStep);
      
      this.showInsertDialog = false;
      this.$toast?.success('Step inserted successfully!') || console.log('✅ Step inserted successfully!');
    },

    getDefaultStepName(type) {
      const names = {
        task: 'New Task',
        decision: 'New Decision',
        approval: 'New Approval',
        notification: 'New Notification'
      };
      return names[type] || 'New Step';
    },

    getDefaultStepDescription(type) {
      const descriptions = {
        task: 'Task description',
        decision: 'Decision point in workflow',
        approval: 'Approval process',
        notification: 'Send notification'
      };
      return descriptions[type] || 'Step description';
    },

    getDefaultDueDate(type) {
      const dueDates = {
        task: '7',
        decision: '3',
        approval: '5',
        notification: '1'
      };
      return dueDates[type] || '7';
    },

    // Drag and drop methods
    handleDragStart(index, event) {
      this.draggedStepIndex = index;
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/html', event.target.outerHTML);
      event.target.style.opacity = '0.5';
    },

    handleDragEnd(event) {
      event.target.style.opacity = '1';
      this.draggedStepIndex = null;
      this.dragOverIndex = null;
      this.dragPosition = null;
    },

    handleDragOver(event) {
      event.preventDefault();
      event.dataTransfer.dropEffect = 'move';
    },

    handleDragEnter(event) {
      event.preventDefault();
    },

    handleDragLeave(event) {
      // Only clear if we're leaving the entire container
      if (!event.currentTarget.contains(event.relatedTarget)) {
        this.dragOverIndex = null;
        this.dragPosition = null;
      }
    },

    handleDragOverAt() {
      event.preventDefault();
      event.dataTransfer.dropEffect = 'move';
    },

    handleDragEnterAt(index, position) {
      event.preventDefault();
      this.dragOverIndex = index;
      this.dragPosition = position;
    },

    handleDragLeaveAt() {
      // Only clear if we're leaving this specific drop zone
      if (!event.currentTarget.contains(event.relatedTarget)) {
        this.dragOverIndex = null;
        this.dragPosition = null;
      }
    },

    handleDrop(event) {
      event.preventDefault();
      this.dragOverIndex = null;
      this.dragPosition = null;
    },

    handleDropAt(targetIndex, position) {
      event.preventDefault();
      
      if (this.draggedStepIndex === null) return;
      
      const draggedIndex = this.draggedStepIndex;
      const step = this.workflowSteps[draggedIndex];
      
      // Remove from original position
      this.workflowSteps.splice(draggedIndex, 1);
      
      // Calculate new position
      let newIndex = targetIndex;
      if (position === 'before') {
        newIndex = targetIndex;
      } else if (position === 'after') {
        newIndex = targetIndex + 1;
      }
      
      // Adjust for removal
      if (draggedIndex < newIndex) {
        newIndex--;
      }
      
      // Insert at new position
      this.workflowSteps.splice(newIndex, 0, step);
      
      // Update selected step index if needed
      if (this.selectedStep === draggedIndex) {
        this.selectedStep = newIndex;
      } else if (this.selectedStep > draggedIndex && this.selectedStep <= newIndex) {
        this.selectedStep--;
      } else if (this.selectedStep < draggedIndex && this.selectedStep >= newIndex) {
        this.selectedStep++;
      }
      
      this.$toast?.success('Step moved successfully!') || console.log('✅ Step moved successfully!');
      
      // Reset drag state
      this.draggedStepIndex = null;
      this.dragOverIndex = null;
      this.dragPosition = null;
    },

    // Helper method to get target step name
    getTargetStepName(targetStepName) {
      if (!targetStepName) return 'End';
      
      // Find the step by name
      const targetStep = this.workflowSteps.find(step => step.name === targetStepName);
      if (targetStep) {
        const stepIndex = this.workflowSteps.indexOf(targetStep);
        return `${stepIndex + 1}. ${targetStepName}`;
      }
      
      return targetStepName;
    },

    // Create example workflow to demonstrate flow visualization
    createExampleWorkflow() {
      console.log('Create example workflow clicked');
      this.resetForm();
      
      // Add example steps
      this.workflowSteps = [
        {
          type: 'task',
          name: 'Initial Review',
          description: 'Review the submitted application',
          assignee: 'Manager',
          dueDate: '3',
          icon: 'mdi-checkbox-marked-circle',
          conditions: [],
          trueAction: 'next',
          falseAction: 'next',
          trueTargetStep: '',
          falseTargetStep: '',
          trueLabel: '',
          falseLabel: ''
        },
        {
          type: 'decision',
          name: 'Approval Required?',
          description: 'Check if the application needs approval',
          assignee: 'System',
          dueDate: '1',
          icon: 'mdi-help-circle',
          conditions: [
            { field: 'amount', operator: '>', value: '1000' }
          ],
          trueAction: 'specific',
          falseAction: 'next',
          trueTargetStep: 'Final Approval',
          falseTargetStep: '',
          trueLabel: 'High Value',
          falseLabel: 'Standard Process'
        },
        {
          type: 'approval',
          name: 'Final Approval',
          description: 'Get final approval from supervisor',
          assignee: 'Supervisor',
          dueDate: '5',
          icon: 'mdi-account-check',
          conditions: [],
          trueAction: 'next',
          falseAction: 'next',
          trueTargetStep: '',
          falseTargetStep: '',
          trueLabel: '',
          falseLabel: ''
        },
        {
          type: 'notification',
          name: 'Send Confirmation',
          description: 'Notify the applicant about the decision',
          assignee: 'System',
          dueDate: '1',
          icon: 'mdi-bell',
          conditions: [],
          trueAction: 'next',
          falseAction: 'next',
          trueTargetStep: '',
          falseTargetStep: '',
          trueLabel: '',
          falseLabel: ''
        }
      ];
      
      this.workflowName = 'Example Approval Workflow';
      this.workflowDescription = 'A sample workflow showing conditional flows and visual connections';
      this.workflowCategory = 'Approval';
      
      this.$toast?.success('Example workflow created! Check the visual flow connections.') || console.log('✅ Example workflow created! Check the visual flow connections.');
    }
  }
};
</script>

<style scoped>
.workflow-canvas {
  position: relative;
  min-height: 300px;
  padding: 20px;
  background: #f5f5f5;
  border-radius: 8px;
}

.workflow-start {
  text-align: center;
  margin-bottom: 20px;
}

.workflow-steps {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  justify-content: center;
  margin: 20px 0;
}

.workflow-step {
  cursor: pointer;
  transition: transform 0.2s;
}

.workflow-step:hover {
  transform: scale(1.05);
}

.workflow-step.selected {
  transform: scale(1.1);
  box-shadow: 0 4px 8px rgba(0,0,0,0.3);
}

.workflow-end {
  text-align: center;
  margin-top: 20px;
}

.v-card {
  transition: all 0.3s ease;
}

.v-card:hover {
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

/* Workflow Canvas and Flow Visualization */
.workflow-canvas {
  position: relative;
  min-height: 400px;
  padding: 20px;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  border-radius: 12px;
  overflow-x: auto;
}

.workflow-flow {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
}

.workflow-step-container {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 15px 0;
  width: 100%;
  max-width: 400px;
}

.workflow-step {
  cursor: move;
  transition: all 0.3s ease;
  user-select: none;
  width: 100%;
}

.workflow-step:hover {
  transform: scale(1.02);
}

.workflow-step.dragging {
  opacity: 0.5;
  transform: rotate(5deg);
}

.workflow-step-container.drag-over {
  background: rgba(25, 118, 210, 0.1);
  border-radius: 8px;
}

/* Flow Connections */
.flow-connections {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 10px 0;
  width: 100%;
}

.flow-arrow {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 5px 0;
  padding: 8px 16px;
  border-radius: 20px;
  transition: all 0.3s ease;
  position: relative;
}

.normal-flow {
  background: rgba(25, 118, 210, 0.1);
  border: 2px solid #1976d2;
  color: #1976d2;
}

.true-flow {
  background: rgba(76, 175, 80, 0.1);
  border: 2px solid #4caf50;
  color: #4caf50;
  margin-right: 20px;
}

.false-flow {
  background: rgba(244, 67, 54, 0.1);
  border: 2px solid #f44336;
  color: #f44336;
  margin-left: 20px;
}

.flow-label {
  font-size: 12px;
  font-weight: 500;
  margin-top: 4px;
}

.flow-target {
  margin-top: 4px;
}

.decision-flows {
  display: flex;
  justify-content: space-between;
  width: 100%;
  margin-top: 10px;
}

.decision-flows .flow-arrow {
  flex: 1;
  margin: 0 5px;
}

/* Drop Zone Styles */
.drop-zone {
  width: 100%;
  min-height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 5px 0;
  border: 2px dashed transparent;
  border-radius: 8px;
  transition: all 0.3s ease;
  opacity: 0;
}

.drop-zone.active {
  border-color: #1976d2;
  background: rgba(25, 118, 210, 0.1);
  opacity: 1;
}

.drop-indicator {
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #1976d2;
  font-size: 12px;
}

.drop-indicator .v-icon {
  margin-bottom: 4px;
}

.workflow-step-container:hover .drop-zone {
  opacity: 0.3;
}

.workflow-step-container:hover .drop-zone.active {
  opacity: 1;
}

/* Flow Animation */
.flow-arrow:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}

.normal-flow:hover {
  background: rgba(25, 118, 210, 0.2);
}

.true-flow:hover {
  background: rgba(76, 175, 80, 0.2);
}

.false-flow:hover {
  background: rgba(244, 67, 54, 0.2);
}

/* Responsive Design */
@media (max-width: 768px) {
  .workflow-canvas {
    padding: 10px;
  }
  
  .decision-flows {
    flex-direction: column;
  }
  
  .decision-flows .flow-arrow {
    margin: 5px 0;
  }
  
  .true-flow, .false-flow {
    margin: 5px 0;
  }
}
</style>
